import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button"]
  static values = { 
    companyId: String,
    jobType: String,
    pollingInterval: { type: Number, default: 3000 }
  }

  connect() {
    this.pollingTimer = null
  }

  disconnect() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
    }
  }

  startJob(event) {
    event.preventDefault()
    
    const button = event.target
    const jobType = button.dataset.jobType
    
    // Store current job type for later use
    this.currentJobType = jobType
    
    // Disable the button and show loading state
    this.disableButton(button, "🔄 Processing...")
    
    // Start polling for job status
    this.startPolling(jobType)
  }

  disableButton(button, text) {
    button.disabled = true
    button.innerHTML = text
    button.classList.add('opacity-50', 'cursor-not-allowed')
  }

  enableButton(button, originalText) {
    button.disabled = false
    button.innerHTML = originalText
    button.classList.remove('opacity-50', 'cursor-not-allowed')
  }

  startPolling(jobType) {
    this.pollingTimer = setInterval(() => {
      this.checkJobStatus(jobType)
    }, this.pollingIntervalValue)
  }

  async checkJobStatus(jobType) {
    try {
      const response = await fetch(`/companies/${this.companyIdValue}/check_job_status?job_type=${jobType}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      const data = await response.json()
      
      if (data.status === 'running') {
        // Job is still running, continue polling
        return
      } else if (data.status === 'completed') {
        // Job completed successfully
        this.handleJobComplete(data.message, 'success')
      } else if (data.status === 'failed') {
        // Job failed
        this.handleJobComplete(data.message, 'error')
      } else if (data.status === 'not_found') {
        // Job not found, might have been cleared
        this.handleJobComplete('Job status not found', 'warning')
      }
    } catch (error) {
      console.error('Error checking job status:', error)
      this.handleJobComplete('Error checking job status', 'error')
    }
  }

  handleJobComplete(message, type) {
    // Stop polling
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
      this.pollingTimer = null
    }

    // Re-enable the button - find by job type
    const jobType = this.currentJobType
    let buttonId
    if (jobType === 'apollo_enrichment') {
      buttonId = 'apollo-enrich-btn'
    } else if (jobType === 'blog_scan') {
      buttonId = 'blog-scan-btn'
    } else {
      buttonId = `${jobType}-btn`
    }
    
    const button = document.getElementById(buttonId)
    if (button) {
      this.enableButton(button, this.getOriginalButtonText(jobType))
    }

    // Show flash message
    this.showFlashMessage(message, type)
  }

  getOriginalButtonText(jobType) {
    const buttonTexts = {
      'apollo_enrichment': 'Enrich with Apollo',
      'blog_scan': 'Scan Blog'
    }
    return buttonTexts[jobType] || 'Process'
  }

  showFlashMessage(message, type) {
    // Create flash message element
    const flashContainer = document.getElementById('flash-messages') || this.createFlashContainer()
    
    const alertClass = {
      'success': 'bg-green-100 border-green-400 text-green-700',
      'error': 'bg-red-100 border-red-400 text-red-700',
      'warning': 'bg-yellow-100 border-yellow-400 text-yellow-700'
    }[type] || 'bg-blue-100 border-blue-400 text-blue-700'

    const flashMessage = document.createElement('div')
    flashMessage.className = `px-4 py-3 rounded border mb-4 ${alertClass}`
    flashMessage.innerHTML = `
      <div class="flex justify-between items-center">
        <span>${message}</span>
        <button onclick="this.parentElement.parentElement.remove()" class="ml-4 text-lg font-bold">&times;</button>
      </div>
    `
    
    flashContainer.appendChild(flashMessage)
    
    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (flashMessage.parentElement) {
        flashMessage.remove()
      }
    }, 10000)
  }

  createFlashContainer() {
    const container = document.createElement('div')
    container.id = 'flash-messages'
    container.className = 'fixed top-4 right-4 z-50 max-w-md'
    document.body.appendChild(container)
    return container
  }
}
